import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import { Logger, Hub, Auth } from "aws-amplify";
import { tryGetVisitorId } from "../analytics/fingerprint.js";
import BrandclubStoreConfigUtils from "../utils/brandclubStoreConfigUtils.js";
import { Logger as Logger$1 } from "../utils/logger.js";
import CrossDomainStorage from "./CrossDomainStorage.js";
const AmplifyAuth = Auth;
const AmplifyHub = Hub;
Logger.LOG_LEVEL = "NONE";
const logger = new Logger$1("Auth", BrandclubStoreConfigUtils.getAppStage());
const sharedVisitorIdCookieName = BrandclubStoreConfigUtils.getAppStage() === "prod" ? "bcVisitorId" : `bcVisitorId-${BrandclubStoreConfigUtils.getAppStage()}`;
const authConfiguredPromise = new Promise((resolve) => {
  const unsubscribe = AmplifyHub.listen("auth", (data) => {
    var _a;
    if (((_a = data == null ? void 0 : data.payload) == null ? void 0 : _a.event) === "configured") {
      unsubscribe();
      resolve();
    }
  });
});
const waitForAuthConfigured = async (timeout = 5e3) => {
  const timeoutPromise = new Promise((_, reject) => {
    logger.debug(`Waiting for auth to be configured with timeout of ${timeout}ms`);
    setTimeout(() => {
      reject(new Error("Auth not configured after timeout"));
    }, timeout);
  });
  return Promise.race([authConfiguredPromise, timeoutPromise]);
};
const getAWSCognitoConfig = async (authConfig, domainName) => {
  try {
    const appStage = BrandclubStoreConfigUtils.getAppStage();
    const authDomain = BrandclubStoreConfigUtils.getLocalStorageDomain(appStage, {
      domainName
    });
    const authOrigin = `https://${authDomain}`;
    if (domainName) {
      logger.debug(`Using auth origin from provided domainName: ${domainName}`);
    } else {
      logger.warn(`Using fallback localStorageDomain: ${authOrigin} as domainName is not provided`);
    }
    const storage = CrossDomainStorage.createInstance(authOrigin, appStage);
    await storage.initialize();
    let cognitoCredentials = void 0;
    return {
      region: authConfig.region,
      userPoolId: authConfig.userPoolId,
      userPoolWebClientId: authConfig.userPoolClientId,
      oauth: {
        domain: authConfig.signInDomain,
        scope: [
          "phone",
          "email",
          "profile",
          "openid",
          "aws.cognito.signin.user.admin"
        ],
        redirectSignIn: `${origin}/signinredirect`,
        redirectSignOut: `${origin}/signoutredirect`,
        responseType: "code"
      },
      storage,
      Auth: {
        domain: authDomain,
        userPoolId: authConfig.userPoolId,
        clientId: authConfig.userPoolClientId,
        identityPoolId: authConfig.identityPoolId,
        userPoolWebClientId: authConfig.userPoolClientId,
        ClientId: authConfig.userPoolClientId,
        region: authConfig.region,
        credentials: (() => {
          if (!authConfig.identityPoolId) {
            return void 0;
          }
          if (!cognitoCredentials) {
            cognitoCredentials = fromCognitoIdentityPool({
              identityPoolId: authConfig.identityPoolId,
              clientConfig: { region: authConfig.region }
            });
          }
          return cognitoCredentials;
        })()
      }
    };
  } catch (err) {
    logger.error("Error getting AWS Cognito config", err);
    throw err;
  }
};
const redeemBrandAppLoginCode = async ({
  apiDomain,
  loginCode,
  signal
}) => {
  if (!apiDomain) {
    apiDomain = BrandclubStoreConfigUtils.getApiHostName();
  } else {
    apiDomain = "https://" + apiDomain;
  }
  const res = await fetch(`${apiDomain}/api-rewards/brandapp/redeemBrandAppLoginCode`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ loginCode }),
    signal
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error("Error redeeming brand app login code");
  }
};
const checkAndHandleLoginCode = async (apiDomain) => {
  var _a, _b;
  const url = new URL(window.location.href);
  const loginCode = url.searchParams.get("bcLoginCode");
  const shouldLogOut = url.searchParams.get("bcLogout") === "1";
  if (!loginCode && !shouldLogOut) {
    return false;
  }
  url.searchParams.delete("bcLoginCode");
  url.searchParams.delete("bcLogout");
  window.history.replaceState({}, document.title, url.toString());
  try {
    if (loginCode && !shouldLogOut) {
      logger.debug(`Found login code in URL, attempting to redeem ${loginCode} from ${apiDomain}`);
      const res = await redeemBrandAppLoginCode({ apiDomain, loginCode });
      if (res.status === "success" && ((_a = res.data) == null ? void 0 : _a.accessToken) && ((_b = res.data) == null ? void 0 : _b.refreshToken)) {
        logger.debug("Login code redeemed successfully");
        const tokenParts = res.data.accessToken.split(".");
        if (tokenParts.length === 3) {
          try {
            const payload = JSON.parse(atob(tokenParts[1]));
            const username = payload.username || payload["cognito:username"] || payload.sub;
            const appStage = BrandclubStoreConfigUtils.getAppStage();
            const storage = CrossDomainStorage.getCurrentInstance();
            if (!storage) {
              logger.error("CrossDomainStorage not initialized when trying to store login tokens");
              return false;
            }
            if (username) {
              const tempAuthData = {
                accessToken: res.data.accessToken,
                accessTokenExpiresIn: res.data.accessTokenExpiresIn,
                refreshToken: res.data.refreshToken,
                refreshTokenExpiresIn: res.data.refreshTokenExpiresIn,
                idToken: res.data.idToken,
                idTokenExpiresIn: res.data.idTokenExpiresIn,
                username,
                timestamp: Date.now()
              };
              storage.setItem(`bc-pending-auth-${appStage}`, JSON.stringify(tempAuthData));
              return true;
            }
          } catch (error) {
            logger.error("Error parsing access token:", error);
          }
        }
      }
    } else if (shouldLogOut) {
      try {
        logger.debug("Found bcLogout in URL, attempting to log out");
        await signOut();
        return true;
      } catch (error) {
        logger.error("Error signing out:", error);
        return true;
      } finally {
        url.searchParams.delete("bcLogout");
        window.history.replaceState({}, document.title, url.toString());
      }
    }
  } catch (error) {
    logger.error("Error redeeming login code:", error);
  }
  return false;
};
const initializeAuth = async (authConfig, domainName) => {
  const cognitoConfig = await getAWSCognitoConfig(authConfig, domainName);
  Auth.configure(cognitoConfig);
  const hasLoginCode = await checkAndHandleLoginCode(domainName);
  const appStage = BrandclubStoreConfigUtils.getAppStage();
  const storage = CrossDomainStorage.getCurrentInstance();
  if (storage) {
    const pendingAuthKey = `bc-pending-auth-${appStage}`;
    const pendingAuthData = storage.getItem(pendingAuthKey);
    if (pendingAuthData) {
      try {
        const authData = JSON.parse(pendingAuthData);
        const clientId = authConfig.userPoolClientId;
        const username = authData.username;
        const keyPrefix = `CognitoIdentityServiceProvider.${clientId}.${username}`;
        storage.setItem(`${keyPrefix}.accessToken`, authData.accessToken);
        storage.setItem(`${keyPrefix}.refreshToken`, authData.refreshToken);
        storage.setItem(`${keyPrefix}.idToken`, authData.idToken);
        storage.setItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`, username);
        const clockDrift = 0;
        storage.setItem(`${keyPrefix}.clockDrift`, clockDrift.toString());
        const tokenParts = authData.accessToken.split(".");
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          const userData = {
            UserAttributes: [
              { Name: "sub", Value: payload.sub || username }
            ],
            Username: username
          };
          for (const key in payload) {
            if (key !== "sub" && key !== "cognito:username") {
              userData.UserAttributes.push({ Name: key, Value: payload[key] });
            }
          }
          storage.setItem(`${keyPrefix}.userData`, JSON.stringify(userData));
        }
        storage.removeItem(pendingAuthKey);
        logger.debug("Successfully processed pending auth tokens");
        Hub.dispatch("auth", {
          event: "signIn",
          data: { username }
        });
        if (hasLoginCode) {
          window.location.reload();
        }
      } catch (error) {
        logger.error("Error processing pending auth tokens:", error);
        storage.removeItem(pendingAuthKey);
      }
    }
  }
  return cognitoConfig;
};
let amplifyAuthPromise = null;
const initializeAmplifyAuth = async (authConfig, domainName) => {
  if (!amplifyAuthPromise) {
    amplifyAuthPromise = (async () => {
      try {
        await initializeAuth(authConfig, domainName);
      } catch (err) {
        logger.error("Error initializing Amplify Auth", err);
        amplifyAuthPromise = null;
      }
    })();
  }
  return amplifyAuthPromise;
};
const getAccessToken = async () => {
  const session = await Auth.currentSession().catch((_e) => {
    return null;
  });
  if (!session) {
    return null;
  }
  const accessToken = session.getAccessToken();
  return accessToken;
};
const getIdToken = async () => {
  const session = await Auth.currentSession().catch((_e) => {
    return null;
  });
  if (!session) {
    return null;
  }
  const idToken = session.getIdToken();
  return idToken;
};
const getRefreshToken = async () => {
  const session = await Auth.currentSession().catch((_e) => {
    return null;
  });
  if (!session) {
    return null;
  }
  return session == null ? void 0 : session.getRefreshToken();
};
let pendingUserSignedInStatePromise = null;
const createAuthResponse = (userInfo) => {
  if (!userInfo || !userInfo.username) {
    return { signedIn: false, userInfo: null };
  }
  return { signedIn: true, userInfo };
};
const getUserSignedInState = async () => {
  if (pendingUserSignedInStatePromise) {
    return pendingUserSignedInStatePromise;
  }
  pendingUserSignedInStatePromise = (async () => {
    try {
      const session = await Auth.currentSession();
      if (!session || !session.isValid()) {
        return createAuthResponse(void 0);
      }
      const userInfo = await Auth.currentUserInfo();
      if (!userInfo || !userInfo.username) {
        return createAuthResponse(void 0);
      }
      return createAuthResponse(userInfo);
    } catch (e) {
      return createAuthResponse(void 0);
    } finally {
      pendingUserSignedInStatePromise = null;
    }
  })();
  return pendingUserSignedInStatePromise;
};
const signOut = async () => {
  try {
    await waitForAuthConfigured();
    await Auth.signOut();
    const jwt = await getJwt();
    if (jwt) {
      logger.error("Failed to sign out, JWT still exists");
    } else {
      logger.debug("Signed out successfully dispatching signOut event and clearing query cache");
      AmplifyHub.dispatch("auth", { event: "signOut" });
    }
  } catch (e) {
    logger.error("Error signing out", e);
  }
};
const getJwt = async () => {
  try {
    const accessToken = await getAccessToken();
    return (accessToken == null ? void 0 : accessToken.getJwtToken()) || "";
  } catch (e) {
    if (e !== "No current user") {
      logger.error("Error getting JWT", e);
    }
    return "";
  }
};
const getVisitorIdHeader = async () => {
  const uniqueVisitorId = await tryGetVisitorId();
  const uniqueVisitorIdFromCookie = getVisitorIdCookie();
  const uniqueVisitorIdToUse = uniqueVisitorIdFromCookie && uniqueVisitorIdFromCookie !== "" ? uniqueVisitorIdFromCookie : uniqueVisitorId;
  const headers = {};
  if (uniqueVisitorIdToUse) {
    headers["x-bc-visitor-id"] = uniqueVisitorIdToUse;
  }
  return headers;
};
const getAuthorizationHeader = async () => {
  const headers = {};
  const sessionToken = await getAccessToken();
  if (sessionToken) {
    headers["Authorization"] = `Bearer ${sessionToken.getJwtToken()}`;
  }
  return headers;
};
const getHeadersWithUniqueVisitorId = async () => {
  const uniqueVisitorId = await tryGetVisitorId();
  const uniqueVisitorIdFromCookie = getVisitorIdCookie();
  const uniqueVisitorIdToUse = uniqueVisitorIdFromCookie && uniqueVisitorIdFromCookie !== "" ? uniqueVisitorIdFromCookie : uniqueVisitorId;
  const jwt = await getJwt();
  const headers = {
    "Content-Type": "application/json"
  };
  if (jwt) {
    headers["Authorization"] = `Bearer ${jwt}`;
  }
  if (uniqueVisitorIdToUse) {
    headers["x-bc-visitor-id"] = uniqueVisitorIdToUse;
  }
  return headers;
};
const setOtpAuth = async (userInfo) => {
  return dispatchSignInComplete(userInfo);
};
const dispatchSignInComplete = (userInfo) => {
  userInfo = {
    ...userInfo,
    ...userInfo.cognitoUserAttributes || {},
    username: userInfo.username || userInfo.userId
  };
  Hub.dispatch("auth", {
    event: "signIn_Complete",
    data: { username: userInfo.username }
  });
};
const getVisitorIdCookie = () => {
  const name = `${sharedVisitorIdCookieName}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
};
export {
  AmplifyAuth,
  AmplifyHub,
  CrossDomainStorage,
  dispatchSignInComplete,
  getAccessToken,
  getAuthorizationHeader,
  getHeadersWithUniqueVisitorId,
  getIdToken,
  getJwt,
  getRefreshToken,
  getUserSignedInState,
  getVisitorIdCookie,
  getVisitorIdHeader,
  initializeAmplifyAuth,
  setOtpAuth,
  signOut,
  waitForAuthConfigured
};
//# sourceMappingURL=index.js.map
